import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import TextEditor from '../components/TextEditor';

function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image: '',
    category: '',
    author: '',
    status: 'draft'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchBlogs();
    fetchCategories();
  }, [navigate, search, categoryFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      params.append('include_drafts', 'true');
      params.append('page', String(currentPage));
      params.append('limit', '50');

      const response = await axios.get(`/api/blogs?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setBlogs(response.data.blogs);
      setPagination(response.data.pagination);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.get('/api/blogs', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { include_drafts: 'true', limit: 1000 }
      });
      const uniqueCategories = [...new Set(response.data.blogs.map(b => b.category).filter(Boolean))];
      setCategories(uniqueCategories.sort());
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      e.target.value = '';
      setImagePreview(null);
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Use filename directly from response
      const filename = response.data.filename;
      
      if (!filename) {
        throw new Error('No filename returned from server');
      }
      
      setFormData(prev => ({ ...prev, image: filename }));
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
      setImagePreview(null);
      e.target.value = '';
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      image: '',
      category: '',
      author: '',
      status: 'draft'
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowAddModal(true);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || '',
      content: blog.content || '',
      excerpt: blog.excerpt || '',
      image: blog.image || '',
      category: blog.category || '',
      author: blog.author || '',
      status: blog.status || 'draft'
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    if (!formData.title || !formData.content || !formData.author) {
      setFormError('Title, content, and author are required');
      setFormLoading(false);
      return;
    }

    try {
      const token = getAccessToken();
      const url = editingBlog ? `/api/blogs/${editingBlog.id}` : '/api/blogs';
      const method = editingBlog ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingBlog(null);
      fetchBlogs();
      fetchCategories();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      setFormError(error.response?.data?.error || 'Failed to save blog');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/blogs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchBlogs();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      alert('Failed to delete blog');
    }
  };

  return (
    <AdminLayout>
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Blog Management</h2>
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>Add New Blog
          </button>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search blogs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Blog List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status"></div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map(blog => (
                  <tr key={blog.id}>
                    <td>{blog.title}</td>
                    <td>{blog.category || '-'}</td>
                    <td>{blog.author}</td>
                    <td>
                      <span className={`badge ${blog.status === 'published' ? 'bg-success' : 'bg-secondary'}`}>
                        {blog.status}
                      </span>
                    </td>
                    <td>{new Date(blog.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(blog)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(blog.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={loading || (pagination.page || 1) <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '2rem auto' }}>
              <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Add New Blog</h5>
                  <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    
                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Author *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.author}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Category</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="e.g., Health Tips, Medical News"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Excerpt</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                        placeholder="Short description (auto-generated if empty)"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Featured Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {/* Show preview of selected file (before or after upload) */}
                      {imagePreview ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">
                            {uploadingImage ? 'Uploading...' : 'Selected image:'}
                          </p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          {formData.image && !uploadingImage && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger ms-2 mt-2"
                              onClick={() => {
                                setFormData({ ...formData, image: '' });
                                setImagePreview(null);
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : formData.image ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Current image:</p>
                          <img 
                            src={formData.image.startsWith('/uploads/') ? formData.image : `/uploads/${formData.image}`} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              console.error('Error loading image:', formData.image);
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ms-2 mt-2"
                            onClick={() => {
                              setFormData({ ...formData, image: '' });
                              setImagePreview(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Content *</label>
                      <TextEditor
                        value={formData.content || ''}
                        onChange={(e) => {
                          const value = typeof e === 'string' ? e : (e?.target?.value || '');
                          setFormData({ ...formData, content: value });
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer" style={{ flexShrink: 0 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowAddModal(false);
                      setImagePreview(null);
                    }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Save Blog'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '2rem auto' }}>
              <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Edit Blog</h5>
                  <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    
                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Author *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.author}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Category</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Excerpt</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Featured Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {/* Show preview of selected file (before or after upload) */}
                      {imagePreview ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">
                            {uploadingImage ? 'Uploading...' : 'Selected image:'}
                          </p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          {formData.image && !uploadingImage && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger ms-2 mt-2"
                              onClick={() => {
                                setFormData({ ...formData, image: '' });
                                setImagePreview(null);
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : formData.image ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Current image:</p>
                          <img 
                            src={formData.image.startsWith('/uploads/') ? formData.image : `/uploads/${formData.image}`} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              console.error('Error loading image:', formData.image);
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ms-2 mt-2"
                            onClick={() => {
                              setFormData({ ...formData, image: '' });
                              setImagePreview(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Content *</label>
                      <TextEditor
                        value={formData.content || ''}
                        onChange={(e) => {
                          const value = typeof e === 'string' ? e : (e?.target?.value || '');
                          setFormData({ ...formData, content: value });
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer" style={{ flexShrink: 0 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowEditModal(false);
                      setImagePreview(null);
                    }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Update Blog'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminBlogs;
